import { hashString } from "./hash-string";
import { getRelativeLuminance } from "./get-relative-luminance";
import { hslToRgb } from "./hsl-to-rgb";
import { rgbToHex } from "./rgb-to-hex";

export type ColorMode = "any" | "dark" | "light";

export interface GetDeterministicHexColorOptions {
  /**
   * Restricts the generated color based on contrast requirements:
   * - "any": No restrictions (default)
   * - "dark": Vibrant, saturated colors at medium lightness (white text readable, luminance < 0.4)
   * - "light": Lightened versions of the same vibrant hues (black text readable, luminance > 0.4)
   */
  mode?: ColorMode;
}

/**
 * Generates a deterministic hex color from any string.
 *
 * The same input string will always produce the same color output.
 * Optionally restricts colors to be suitable for specific text contrast requirements.
 *
 * @param input - The string to generate a color from
 * @param options - Configuration options
 * @returns A hex color string (e.g., "#a1b2c3")
 *
 * @example
 * ```ts
 * // Generate any color
 * getDeterministicHexColor("user@example.com");
 *
 * // Generate a dark color (white text readable)
 * getDeterministicHexColor("user@example.com", { mode: "dark" });
 *
 * // Generate a light color (black text readable)
 * getDeterministicHexColor("user@example.com", { mode: "light" });
 * ```
 */
/**
 * Mixes a 32-bit unsigned integer with a constant seed using xorshift-style
 * bit avalanching to produce an independent-looking derived hash.
 * This breaks correlation between multiple values extracted from the same source hash.
 */
const mixHash = (h: number, seed: number): number => {
  // XOR with seed, then avalanche with multiply + xorshift
  let x = (h ^ seed) >>> 0;
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b) >>> 0;
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b) >>> 0;
  return (x ^ (x >>> 16)) >>> 0;
};

export const getDeterministicHexColor = (input: string, options: GetDeterministicHexColorOptions = {}): string => {
  const { mode = "any" } = options;

  const hash = hashString(input);

  // Derive three independent hashes for hue, saturation, and lightness.
  // Using mixHash prevents the correlated bit-shifting that causes UUID color clustering,
  // since UUIDs share fixed nibbles (version=4, variant=8/9/a/b) that bias the raw hash.
  const hueHash = mixHash(hash, 0x9e3779b9);
  const satHash = mixHash(hash, 0x6c62272e);
  const litHash = mixHash(hash, 0xf4cfd70c);

  const hue = hueHash % 360;
  // High saturation (80-100%) for vibrant colors across all modes
  const saturation = 0.8 + (satHash % 20) / 100;

  let lightness: number;

  switch (mode) {
    case "dark":
      // Lightness 45-55%: the sweet spot for maximum vibrancy (white text readable)
      lightness = 0.45 + (litHash % 10) / 100;
      break;
    case "light":
      // Lightness 75-85%: lightened version of vibrant hues (black text readable)
      lightness = 0.75 + (litHash % 10) / 100;
      break;
    default:
      // Full range 45-80% for balanced colors
      lightness = 0.45 + (litHash % 35) / 100;
  }

  const [r, g, b] = hslToRgb(hue, saturation, lightness);

  // Verify luminance meets requirements and adjust if needed
  const luminance = getRelativeLuminance(r, g, b);

  if (mode === "dark" && luminance >= 0.3) {
    // Some hues (cyan, green, yellow) are inherently bright; step down until luminance < 0.4
    let adjustedLightness = lightness * 0.85;
    let ar: number, ag: number, ab: number;
    let adjustedLuminance: number;
    do {
      [ar, ag, ab] = hslToRgb(hue, saturation, adjustedLightness);
      adjustedLuminance = getRelativeLuminance(ar, ag, ab);
      adjustedLightness *= 0.9;
    } while (adjustedLuminance >= 0.4 && adjustedLightness > 0.05);
    return rgbToHex(ar!, ag!, ab!);
  }

  if (mode === "light" && luminance <= 0.4) {
    // Color is too dark, push lightness up while keeping saturation
    const adjustedLightness = 0.78 + (litHash % 7) / 100;
    const [ar, ag, ab] = hslToRgb(hue, saturation, adjustedLightness);
    return rgbToHex(ar, ag, ab);
  }

  return rgbToHex(r, g, b);
};
